import { useEffect, useState, useRef } from "react";
import firestoreService from "../../services/firestoreService";
import { useParams } from "react-router-dom";
import { useAuth } from "../auth/useAuth";
import { formatTime } from "../../utils/TimeUtils";


const useLectureVideo = (nav, onNavChangeHandler) => {
    const { id } = useParams();
    const { user } = useAuth();
    const videoRef = useRef(null);
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [modalProps, setModalProps] = useState({
        title:"",
        message:"",
        onConfirm:null,
        onCancel:null,
        confirmButtonName:"",
        cancelButtonName:""
    })
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [videoList, setVideoList] = useState([]);
    const [nowVideoIndex, setNowVideoIndex] = useState(0);
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);
    const [watchStartTime, setWatchStartTime] = useState(null);
    const [totalWatchTime, setTotalWatchTime] = useState(0);
    const [isWatching, setIsWatching] = useState(false);
    const [isMemoOpen, setIsMemoOpen] = useState(false);
    const [memoText, setMemoText] = useState('');
    const [memoCanvasData, setMemoCanvasData] = useState(null);

    const fetchVideoData = async() => {
        const result = await firestoreService.getCollection(`LectureVideo/Lecture/${id}`);
        if(result.success){
            setVideoList(result.data);
            return;
        }
        setIsOpenModal(true);
        setModalProps({
            title:"에러",
            message:"영상 데이터를 가져오는 중 오류가 발생하였습니다",
            onConfirm:() => setIsOpenModal(false),
            confirmButtonName:"확인"
        })
    }

    useEffect(() => {
        fetchVideoData();
    }, [id])

    // 전체화면 상태 변경 감지
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement
            );
            setIsFullscreen(isCurrentlyFullscreen);
        };

        // 다양한 브라우저의 전체화면 이벤트 리스너 추가
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    // 비디오 이벤트 핸들러들
    const handleLoadedMetadata = () => {
        if (videoRef.current) {
            const videoDuration = videoRef.current.duration;
            if (videoDuration && !isNaN(videoDuration)) {
                setDuration(videoDuration);
                onNavChangeHandler({
                    ...nav,
                    duration: formatTime(videoDuration)
                });
                setIsVideoLoaded(true);
            }
        }
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            const current = videoRef.current.currentTime;
            const dur = videoRef.current.duration;
            
            if (dur && !isNaN(dur) && duration !== dur) {
                setDuration(dur);
            }
            
            setCurrentTime(current);
            if (dur && !isNaN(dur)) {
                setProgress((current / dur) * 100);
            }
        }
    };

    const handlePlay = () => {
        setIsPlaying(true);
        setIsWatching(true);
        setWatchStartTime(Date.now());
    };

    const handlePause = () => {
        setIsPlaying(false);
        if (isWatching && watchStartTime) {
            const watchDuration = Math.floor((Date.now() - watchStartTime) / 1000);
            setTotalWatchTime(prev => prev + watchDuration);
            // 상태 초기화는 하지 않고, handleEnded에서 처리하도록 함
        }
    };

    const handleEnded = async () => {
        setIsPlaying(false);
        
        // 시청 시간 계산 및 저장 - 현재 상태를 먼저 확인
        let finalWatchTime = totalWatchTime;
        
        if (watchStartTime && isWatching) {
            const watchDuration = Math.floor((Date.now() - watchStartTime) / 1000);
            finalWatchTime = totalWatchTime + watchDuration;
        }
        
        // 상태 초기화
        setIsWatching(false);
        setWatchStartTime(null);
        setTotalWatchTime(finalWatchTime);
        
        // Firestore에 시청 시간 저장
        if (finalWatchTime > 0) {
            await saveWatchTime(finalWatchTime);
        }
        
        // 다음 비디오로 자동 이동
        if (nowVideoIndex < videoList.length - 1) {
            setNowVideoIndex(nowVideoIndex + 1);
            setTotalWatchTime(0); // 다음 비디오를 위해 초기화
        }
        if(nowVideoIndex === videoList.length - 1){
            onNavChangeHandler({
                ...nav,
                completed: true
            });
        }
    };

    const handlePlayPause = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
        }
    };

    const handleProgressClick = (event) => {
        if (videoRef.current && duration > 0) {
            const rect = event.currentTarget.getBoundingClientRect();
            const clickX = event.clientX - rect.left;
            const width = rect.width;
            const newProgress = (clickX / width) * 100;
            const newTime = (newProgress / 100) * duration;
            
            setProgress(newProgress);
            setCurrentTime(newTime);
            videoRef.current.currentTime = newTime;
        }
    };

    const handleVolumeChange = (newVolume) => {
        if (videoRef.current) {
            videoRef.current.volume = newVolume;
            setVolume(newVolume);
            setIsMuted(newVolume === 0);
        }
    };

    const handleMuteToggle = () => {
        if (videoRef.current) {
            if (isMuted) {
                videoRef.current.volume = volume;
                setIsMuted(false);
            } else {
                videoRef.current.volume = 0;
                setIsMuted(true);
            }
        }
    };

    const handleFullscreen = () => {
        if (videoRef.current) {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement
            );

            if (!isCurrentlyFullscreen) {
                // 전체화면 진입
                if (videoRef.current.requestFullscreen) {
                    videoRef.current.requestFullscreen().catch(() => {});
                } else if (videoRef.current.webkitRequestFullscreen) {
                    videoRef.current.webkitRequestFullscreen();
                } else if (videoRef.current.mozRequestFullScreen) {
                    videoRef.current.mozRequestFullScreen();
                } else if (videoRef.current.msRequestFullscreen) {
                    videoRef.current.msRequestFullscreen();
                }
            } else {
                // 전체화면 해제
                if (document.exitFullscreen) {
                    document.exitFullscreen().catch(() => {});
                } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen();
                } else if (document.mozCancelFullScreen) {
                    document.mozCancelFullScreen();
                } else if (document.msExitFullscreen) {
                    document.msExitFullscreen();
                }
            }
        }
    };

    const handleVideoChange = (index) => {
        if (index >= 0 && index < videoList.length) {
            setNowVideoIndex(index);
            setCurrentTime(0);
            setProgress(0);
            setDuration(0);
            setIsPlaying(false);
            setIsVideoLoaded(false);
        }
    };

    const handleNextVideo = () => {
        if (nowVideoIndex < videoList.length - 1) {
            handleVideoChange(nowVideoIndex + 1);
        }
    };

    const handlePrevVideo = () => {
        if (nowVideoIndex > 0) {
            handleVideoChange(nowVideoIndex - 1);
        }
    };

    // 메모 불러오기
    const fetchMemo = async () => {
        try {
            if (!user?.uid || !id) return;
            
            const videoId = videoList[nowVideoIndex]?.id;
            if (!videoId) return;
            
            const docPath = `LectureProgress/${id}/LectureMemo`;
            const result = await firestoreService.getDocument(docPath, `${user.uid}_${videoId}`);
            
            if (result.success && result.data) {
                setMemoText(result.data.text || '');
                setMemoCanvasData(result.data.canvas_data || null);
            }
        } catch (error) {
            // 메모 불러오기 오류 처리
        }
    };

    // 메모 저장
    const saveMemo = async (text, canvasData) => {
        try {
            if (!user?.uid || !id) {
                return { success: false, error: '사용자 정보가 없습니다.' };
            }
            
            const videoId = videoList[nowVideoIndex]?.id;
            if (!videoId) {
                return { success: false, error: '비디오 정보가 없습니다.' };
            }
            
            const docPath = `LectureProgress/${id}/LectureMemo`;
            const docId = `${user.uid}_${videoId}`;
            
            // 데이터 정리: null이나 undefined를 제거하고 안전한 값만 전달
            const memoData = {
                text: text || '',
                video_id: videoId,
                video_title: videoList[nowVideoIndex]?.title || '',
                video_index: typeof nowVideoIndex === 'number' ? nowVideoIndex : 0
            };
            
            // canvas_data가 있을 때만 추가
            if (canvasData && typeof canvasData === 'string') {
                memoData.canvas_data = canvasData;
            }
            
            const existingData = await firestoreService.getDocument(docPath, docId);
            
            let result;
            if (existingData.success && existingData.data) {
                // 업데이트 시 registered_at은 변경하지 않음 (기존 값 유지)
                result = await firestoreService.updateDocument(docPath, docId, memoData);
            } else {
                // 새로 생성할 때만 registered_at 추가
                result = await firestoreService.createDocument(docPath, {
                    ...memoData,
                    registered_at: new Date()
                }, docId);
            }
            
            if (result && result.success) {
                return { success: true, error: null };
            } else {
                return { success: false, error: result?.error || '저장에 실패했습니다.' };
            }
        } catch (error) {
            return { success: false, error: error.message || '저장 중 오류가 발생했습니다.' };
        }
    };

    // 비디오 변경 시 메모 불러오기
    useEffect(() => {
        if (videoList.length > 0 && nowVideoIndex >= 0 && videoList[nowVideoIndex]?.id) {
            fetchMemo();
        }
    }, [nowVideoIndex, videoList.length, id, user?.uid]);

    // 시청 시간을 Firestore에 저장하는 함수
    const saveWatchTime = async (watchTimeInSeconds) => {
        try {
            if (!user?.uid) {
                return;
            }

            const videoData = {
                watch_time_seconds: watchTimeInSeconds,
                video_duration: duration,
                watch_percentage: duration > 0 ? Math.round((watchTimeInSeconds / duration) * 100) : 0,
                video_title: videoList[nowVideoIndex]?.title || 'Unknown',
                video_index: nowVideoIndex,
                completed_at: new Date(),
                updated_at: new Date()
            };

            // 기존 데이터가 있는지 확인
            const docPath = `LectureProgress/${id}/LectureVideo`;
            const existingData = await firestoreService.getDocument(docPath, user.uid);
            
            let result;
            if (existingData.success && existingData.data) {
                // 기존 데이터가 있으면 업데이트
                result = await firestoreService.updateDocument(docPath, {
                    ...videoData,
                    registered_at: existingData.data.registered_at || new Date()
                }, user.uid);
            } else {
                // 기존 데이터가 없으면 새로 생성
                result = await firestoreService.createDocument(docPath, {
                    ...videoData,
                    registered_at: new Date()
                }, user.uid);
            }
            
            if (!result.success) {
                // 시청 시간 저장 실패 처리
            }
        } catch (error) {
            // 시청 시간 저장 중 오류 처리
        }
    };

    return{
        isOpenModal,
        modalProps,
        setIsOpenModal,
        setModalProps,
        isPlaying,
        currentTime,
        progress,
        duration,
        volume,
        isMuted,
        isFullscreen,
        videoList,
        nowVideoIndex,
        videoRef,
        handlePlayPause,
        handleProgressClick,
        handleLoadedMetadata,
        handleTimeUpdate,
        handlePlay,
        handlePause,
        handleEnded,
        handleVolumeChange,
        handleMuteToggle,
        handleFullscreen,
        handleVideoChange,
        handleNextVideo,
        handlePrevVideo,
        isVideoLoaded,
        totalWatchTime,
        isWatching,
        isMemoOpen,
        setIsMemoOpen,
        memoText,
        setMemoText,
        memoCanvasData,
        setMemoCanvasData,
        saveMemo,
        fetchMemo,
    }
}

export default useLectureVideo;